import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import CopyableApiKeyValue from 'app/frontend/base/components/CopyableApiKeyValue'
import { resetCalendarToken } from 'app/frontend/base/api/user'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RotateCcw } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const SubscribeCalendarDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    data: { user },
  } = usePageInfo()

  const httpsUrl = useMemo(() => {
    if (!user?.calendarToken) return ''
    return `${window.location.origin}/calendar/${user.calendarToken}.ics`
  }, [user?.calendarToken])

  const showWebcal = useMemo(
    () => /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent),
    [],
  )

  const webcalUrl = useMemo(() => {
    if (!showWebcal || !user?.calendarToken) return ''
    return `webcal://${window.location.host}/calendar/${user.calendarToken}.ics`
  }, [showWebcal, user?.calendarToken])

  const { mutate: resetToken, isPending } = useMutation({
    mutationFn: () => {
      if (!user) throw new Error('missing user')
      return resetCalendarToken(user.id)
    },
    onSuccess: (updatedUser) => {
      queryClient.setQueryData<wowaudit.PageInfo>(['pageInfo'], (existing) => {
        if (!existing?.user) return existing
        return {
          ...existing,
          user: {
            ...existing.user,
            calendarToken: updatedUser.calendarToken,
          },
        }
      })
      toast.success(t('profile.events.subscribe-reset-success'))
    },
    onError: (error) => displayErrorToast(error, t),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='max-w-lg'
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('profile.events.subscribe-title')}</DialogTitle>
          <DialogDescription>
            {t('profile.events.subscribe-description')}
          </DialogDescription>
        </DialogHeader>

        <CopyableApiKeyValue
          value={httpsUrl}
          tooltipContent={t('dashboard.team.settings.click-to-copy')}
        />

        {showWebcal && webcalUrl ? (
          <a
            href={webcalUrl}
            className='text-sm text-primary hover:text-primary/90 justify-self-start'
          >
            {t('profile.events.subscribe-webcal')}
          </a>
        ) : null}

        <DialogFooter className='sm:justify-between'>
          <Button
            type='button'
            variant='ghost'
            className='flex gap-2'
            disabled={isPending || !user}
            onClick={() => resetToken()}
          >
            <RotateCcw size={14} />
            {t('profile.events.reset-link')}
          </Button>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
          >
            {t('profile.events.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default SubscribeCalendarDialog
